import {
  getCurrentEvent,
  getEventsById,
} from "core-integration/src/store/events/reducers";
import { IEvent } from "core-integration/src/store/events/types";
import { formatRawAsLocal } from "core-integration/src/utils/datetime";
import Dialog from "plos/src/components/Dialog";
import HorizontalDivider from "plos/src/components/HorizontalDivider";
import Link from "plos/src/components/links/Link";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  AdobeTeamSavedContent,
  ChallengeTeamSavedContent,
  DefaultTeamSavedContent,
  LastFanStandingContent,
} from "./promotions";
import styles from "./TeamSavedModal.module.css";
import type { TeamSavedPromotion } from "./types";

interface BaseModalProps {
  isOpen: boolean;
  closeDialog: () => void;
  children: ReactNode;
  currentEvent: IEvent | null;
  headerVariant?: "standard" | "gradient";
}

const TeamSavedModalBase = ({
  isOpen,
  closeDialog,
  children,
  currentEvent,
  headerVariant = "standard",
}: BaseModalProps) => {
  const { t } = useTranslation("pickTeam");
  const eventsById = useSelector(getEventsById);

  return (
    <Dialog
      isOpen={isOpen}
      handleClose={closeDialog}
      headerVariant={headerVariant}
      label={t("teamSavedModal.label", "Team Saved")}
    >
      <div className={styles.teamSavedModalContainer}>
        {children}
        {/* Show the chip warning when picking team for Gameweek 17, 18 and 19 */}
        {currentEvent && currentEvent.id >= 17 && currentEvent.id <= 19 && (
          <>
            <HorizontalDivider />
            <strong>{t("useYourChips.title", "Use Your Chips")}</strong>
            <p>
              {t(
                "useYourChips.desc",
                "Use them or lose them! FPL chips will be refreshed in Gameweek 20 - use your existing chips before the Gameweek 19 deadline of {{deadline}}.",
                { deadline: formatRawAsLocal(eventsById[19].deadline_time) },
              )}
            </p>
            <p>
              <Link
                href={`${
                  import.meta.env.VITE_HF_BASE
                }/en/news/4362085/when-to-use-your-remaining-chips-in-fpl-before-the-gw19-cut-off`}
              >
                {t(
                  "useYourChips.learnMore",
                  "Learn more about the chip refresh.",
                )}
              </Link>
            </p>
          </>
        )}
      </div>
    </Dialog>
  );
};

interface TeamSavedModalViewProps {
  isOpen: boolean;
  closeDialog: () => void;
  currentEvent: IEvent | null;
  currentPromotion: TeamSavedPromotion;
  handleCreateCrest: (img: Blob, prompt: string) => Promise<void>;
}

export const TeamSavedModalView = ({
  isOpen,
  closeDialog,
  currentPromotion,
  currentEvent,
  handleCreateCrest,
}: TeamSavedModalViewProps) => {
  const baseProps = { isOpen, closeDialog, currentEvent };

  switch (currentPromotion) {
    case "adobe":
      return (
        <TeamSavedModalBase {...baseProps} headerVariant="gradient">
          <AdobeTeamSavedContent
            handleCreateCrest={handleCreateCrest}
            closeDialog={closeDialog}
          />
        </TeamSavedModalBase>
      );
    case "challenge":
      return (
        <TeamSavedModalBase {...baseProps}>
          <ChallengeTeamSavedContent />
        </TeamSavedModalBase>
      );
    case "lastFanStanding":
      return (
        <TeamSavedModalBase {...baseProps}>
          <LastFanStandingContent />
        </TeamSavedModalBase>
      );
    default:
      return (
        <TeamSavedModalBase {...baseProps}>
          <DefaultTeamSavedContent />
        </TeamSavedModalBase>
      );
  }
};

interface TeamSavedModalProps {
  handleCreateCrest: (img: Blob, prompt: string) => Promise<void>;
  currentPromotion: TeamSavedPromotion | null;
  onClose: () => void;
}

const TeamSavedModal = ({
  handleCreateCrest,
  currentPromotion,
  onClose,
}: TeamSavedModalProps) => {
  const currentEvent = useSelector(getCurrentEvent);

  return (
    <TeamSavedModalView
      isOpen={currentPromotion !== null}
      closeDialog={onClose}
      currentEvent={currentEvent}
      currentPromotion={currentPromotion ?? "default"}
      handleCreateCrest={handleCreateCrest}
    />
  );
};

export default TeamSavedModal;
